import { Maybe } from '@monetize/types/common';
import { formatCurrency } from '@monetize/utils/core';
import {
  IQuoteItemRespSchema,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
  ProductTypeEnum,
} from '../../types';
import {
  getPricePerUnitWithRateFrequencyShortCode,
  getUnitPriceAfterDiscount,
} from '../../utils/quotes';
import { getUsageProductPriceForUpliftPPU } from '../../utils/upliftPrice.utils';
import { MText, MTooltip } from '../Monetize';

type PriceUpliftPpuProps = {
  item: IQuoteItemRespSchema;
  quote: IQuoteRespSchema;
  quotePrices: IQuotePrice[];
  offeringRate?: IRateResSchema;
  productType?: ProductTypeEnum;
  parentQuoteOfferingId?: string;
  rateId?: string;
  upliftPercentage?: number;
  /** Product's `ppuDisplayPrecision`; falls back to the currency default. */
  displayPrecision?: Maybe<number>;
  textProps?: React.ComponentProps<typeof MText>;
};

export const PriceUpliftPpu = ({
  item,
  quote,
  quotePrices,
  offeringRate,
  productType,
  parentQuoteOfferingId,
  rateId,
  displayPrecision,
  textProps,
}: PriceUpliftPpuProps) => {
  let itemPriceForPpu = '';
  let usagePrice = '';

  if (item.productType === ProductTypeEnum.USAGE) {
    usagePrice = getUsageProductPriceForUpliftPPU(item);
    if (usagePrice === '*') {
      itemPriceForPpu = usagePrice;
    } else {
      itemPriceForPpu = formatCurrency(usagePrice, {
        currency: quote?.currency ?? '',
        maximumFractionDigits: displayPrecision ?? 6,
      });
    }
  } else {
    itemPriceForPpu = getUnitPriceAfterDiscount({
      parentQuoteOfferingId,
      item,
      quotePrices,
      currency: quote?.currency ?? '',
      offeringId: offeringRate?.offering.id,
      rateId,
      displayPrecision,
    });
  }

  const priceStr = getPricePerUnitWithRateFrequencyShortCode({
    priceStr: itemPriceForPpu,
    offeringRate,
    productType,
    isCustomPrice: false,
    item,
  });

  if (item.productType === ProductTypeEnum.USAGE && usagePrice === '*') {
    return (
      <MTooltip label="The price per unit will be determined based on the actual consumed usage in a given billing period">
        <MText textAlign="right" {...textProps}>
          {priceStr}
        </MText>
      </MTooltip>
    );
  }

  return (
    <MText textAlign="right" {...textProps}>
      {priceStr}
    </MText>
  );
};
